import { Tooltip } from '@mui/material';
import { GridRenderCellParams, GridTreeNodeWithRender } from '@mui/x-data-grid';
import { useCallback, useEffect, useRef, useState } from 'react';

/** Properties for the GridItemRenderOverflow */
interface GridItemRenderOverflowProps {
  /**
   * The contents of the grid item. Must be a string
   */
  text: string;
}

/**
 * This component exists to display cells within the grid item that could be
 *  too long. When they are too long, a tooltip will show to show the content.
 */
const GridItemRenderOverflow = (props: GridItemRenderOverflowProps) => {
  const spanRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const checkTruncation = useCallback(() => {
    if (spanRef.current) {
      setIsTruncated(spanRef.current.scrollWidth > spanRef.current.clientWidth);
    }
  }, []);

  useEffect(() => {
    // Check truncation initially
    checkTruncation();

    // Recheck truncation on window resize
    window.addEventListener('resize', checkTruncation);

    return () => {
      window.removeEventListener('resize', checkTruncation);
    };
  }, [checkTruncation]);

  // Also update on text changes
  useEffect(() => {
    checkTruncation();
  }, [props.text, checkTruncation]);

  const content = (
    <span
      ref={spanRef}
      style={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        width: '100%',
        display: 'block',
      }}
    >
      {props.text}
    </span>
  );

  return isTruncated ? (
    <Tooltip title={props.text} arrow>
      {content}
    </Tooltip>
  ) : (
    content
  );
};

export default GridItemRenderOverflow;

/**
 * A method to use with the react grid to render the cell with overflow
 */
export const renderWithOverflow = (
  params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>
) => {
  const value = params.value || '';
  return <GridItemRenderOverflow text={value} />;
};
